import Big from 'big.js';
import {Observable, combineLatest} from 'rxjs';
import {map, throttleTime} from 'rxjs/operators';
import {div, gt, minus, numUtil, plus, pow10, times, toFixed} from '@/helper/num';
import {calcValueBalanceUtils} from '@/helper/calc-value-balance';
import {calcMoneyData$} from '@/calc/fiat';
import {currenciesCache$} from '@/config';
import {DEFAULT_USD_PRICE_SCALE} from '@/const/data-config';
import {indexPricesCache$} from '@/price-index/price-index';
import {allSubWalletsCache$} from '@/spot-wallets/sub-users-all/sub-users-assets';
import {lendingAccountsCache$} from './accounts-cache';
import type {PriceEntity, CalcMoneyData, LendingAccount, CurrencyEntity, LendingAccountRaw} from '@/types';

export const lendingAccounts$: Observable<LendingAccount[]> = combineLatest([
  lendingAccountsCache$,
  indexPricesCache$,
  calcMoneyData$,
  currenciesCache$,
  allSubWalletsCache$,
]).pipe(throttleTime(100), map(convertAccounts));

function convertAccounts([cache, indexPricesCache, calcMoneyData, currenciesCache]: [
  Map<string, LendingAccountRaw>,
  Map<string, PriceEntity>,
  CalcMoneyData,
  Map<string, CurrencyEntity>,
  Map<string, number>
]): LendingAccount[] {
  const lendingAccounts = cache
    .toArray((account: LendingAccountRaw) => convertAccount(account, indexPricesCache, calcMoneyData, currenciesCache))
    .filter(Boolean)
    .sort((a, b) => (gt(b.totalBalanceValue, a.totalBalanceValue) ? 1 : -1));
  return lendingAccounts;
}

function convertAccount(
  {
    currency,
    currencyCode,
    collateralAmountRq: collateralBalanceEv,
    loanAmountRq: borrowedEv,
    interestAmountRq: cumInterestEv,
    balanceRq: balanceEv,
    lockedWithdrawRq: lockedWithdrawEv,
    supplyAmountRq: supplyAmountEv,
    supplyTotalInterestRq: supplyTotalInterestEv,
    autoInvest,
  }: LendingAccountRaw,
  indexPricesCache: Map<string, PriceEntity>,
  calcMoneyData: CalcMoneyData,
  currenciesCache: Map<string, CurrencyEntity>
): LendingAccount {
  const currencyInfo = currenciesCache.get(currency);
  if (!currencyInfo || !currencyInfo.inAssetsDisplay) {
    return null;
  }

  const usdCurrency = currenciesCache.get('USD');
  const btcCurrency = currenciesCache.get('BTC');

  const indexPrice = indexPricesCache.get('.' + currency);
  const usdtRate = indexPricesCache.get('.USDT');
  const btcRate = indexPricesCache.get('.BTC');
  if (!indexPrice || !btcCurrency || !usdCurrency || !usdtRate || !btcRate) {
    // TODO: 当相关价格没有拿到时，不推送相关account
    return null;
  }

  const {valuePrecision, name, displayCurrency} = currencyInfo;

  const availableBalanceEv = minus(balanceEv, lockedWithdrawEv);
  const availableBalance = calcBalance(availableBalanceEv, valuePrecision);
  const availableBalance2 = calcBalance(availableBalanceEv, 2);
  const availableBalance4 = calcBalance(availableBalanceEv, 4);
  const [availableBalanceMoneyWU] = calcMoneyData(displayCurrency, availableBalanceEv, true);

  const indexPriceEp = div(indexPrice.priceEp, pow10(DEFAULT_USD_PRICE_SCALE));

  const {valueFactor: usdValueFactor, valuePrecision: usdValuePrecision} = usdCurrency;
  const {valuePrecision: btcValuePrecision} = btcCurrency;

  const totalBalanceEv = plus(plus(balanceEv, collateralBalanceEv), supplyAmountEv);
  const [totalBalanceMoneyWU, totalBalanceValue] = calcMoneyData(displayCurrency, totalBalanceEv, true);
  const totalBalanceUsdEv = convertBalanceToUsdEvByPrice(totalBalanceEv, indexPrice);
  const totalBalanceUsdtEv = Big(totalBalanceUsdEv).times(usdValueFactor).div(usdtRate.priceEp);
  const totalBalanceBtcEv = Big(totalBalanceUsdEv).times(usdValueFactor).div(btcRate.priceEp);

  const totalDebtEv = plus(borrowedEv, cumInterestEv);
  const [totalDebtMoneyWU, totalDebtValue] = calcMoneyData(displayCurrency, totalDebtEv, true);
  const netEquityEv = minus(totalBalanceEv, totalDebtEv);
  const [netEquityMoneyWU, netEquityValue] = calcMoneyData(displayCurrency, netEquityEv, true);

  const supplyAmountUsdEv = convertBalanceToUsdEvByPrice(supplyAmountEv, indexPrice);

  return {
    currency,
    currencyCode,
    displayCurrency,
    name,
    totalBalanceEv,
    totalBalance: calcBalance(totalBalanceEv, valuePrecision),
    totalBalanceMoneyWU,
    totalBalanceValue,
    availableBalanceEv,
    availableBalance,
    availableBalanceWU: availableBalance + ' ' + currency,
    availableBalance2,
    availableBalance2WU: availableBalance2 + ' ' + currency,
    availableBalance4,
    availableBalance4WU: availableBalance4 + ' ' + currency,
    availableBalanceMoneyWU,
    collateralBalanceEv,
    collateralBalance: calcBalance(collateralBalanceEv, valuePrecision),
    lockedWithdrawEv,
    lockedWithdraw: calcBalance(lockedWithdrawEv, valuePrecision),
    borrowedEv,
    borrowed: calcBalanceCeil(borrowedEv, valuePrecision),
    cumInterestEv,
    cumInterest: calcBalanceCeil(cumInterestEv, valuePrecision),
    totalDebtEv,
    totalDebt: calcBalanceCeil(totalDebtEv, valuePrecision),
    totalDebtValue,
    totalDebtMoneyWU,
    netEquityEv,
    netEquity: calcBalance(netEquityEv, valuePrecision),
    netEquityValue,
    netEquityMoneyWU,
    indexPriceEp,
    totalBalanceUsdt: numUtil.delimit(numUtil.floor(totalBalanceUsdtEv, usdValuePrecision)),
    totalBalanceBtc: numUtil.delimit(numUtil.floor(totalBalanceBtcEv, btcValuePrecision)),
    supplyAmountEv,
    supplyAmount: calcBalance(supplyAmountEv, valuePrecision),
    supplyAmountUsdEv,
    supplyAmountUsd: calcBalance(supplyAmountUsdEv, valuePrecision),
    supplyTotalInterestEv,
    supplyTotalInterest: calcBalance(supplyTotalInterestEv, valuePrecision),
    autoInvest,
  };
}

function convertBalanceToUsdEvByPrice(balanceEv, indexPrice): string {
  if (!indexPrice) {
    return '0';
  }

  const balanceFactor = 1;
  const valueFactor = 1;
  return calcValueBalanceUtils.getValueEv(
    balanceEv,
    indexPrice.priceEp,
    balanceFactor,
    indexPrice.priceFactor,
    valueFactor,
    false
  ) as string;
}

function calcBalance(balanceEv, valuePrecision): string {
  return numUtil.delimit(toFixed(balanceEv, valuePrecision, 0));
}

function calcBalanceCeil(balanceEv, valuePrecision): string {
  return numUtil.delimit(toFixed(balanceEv, valuePrecision, 3));
}
